'use client';

import React, { useState } from 'react';
import { TreatingVolumeBar } from './components';
import { colors } from '../../constants';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useGetCodeSetsVolumeQuery } from 'src/api/redux';
import useProfileSelections from 'src/hooks/profiles/useProfileSelections';
import {
  PROFILE_VOLUME_MIN_YEAR,
  PROFILE_VOLUME_MAX_YEAR,
} from 'src/components/constants';
interface ProfileTreatingVolumeChartProps {
  location?: 'hcp' | 'center' | 'clinic' | 'system' | 'site_of_care';
}

// Check if value is masked (e.g., "< 11", "<11", "< 11*")
const isMaskedValue = (value: string): boolean => {
  if (typeof value !== 'string') return false;
  return /^<\s*\d+/.test(value.trim());
};

const ProfileTreatingVolumeChart: React.FC<ProfileTreatingVolumeChartProps> = ({
  location = 'hcp',
}) => {
  const { entityDetails } = useProfile();
  const { selectedFilterSet, setSelectedFilterSet, isAllCodeSets } =
    useProfileSelections(location);

  // fetch code sets volume data
  const { data: volumeDataResponse } = useGetCodeSetsVolumeQuery(
    {
      entityId: entityDetails?.id || '',
      params: {
        code_set_ids: selectedFilterSet?.id || [],
        all_code_sets: isAllCodeSets,
        min_year: PROFILE_VOLUME_MIN_YEAR,
        max_year: PROFILE_VOLUME_MAX_YEAR,
      },
    },
    {
      skip: !entityDetails?.id,
    }
  );

  const [showZeroCodeSets, setShowZeroCodeSets] = useState(false);

  const handleResetFilters = () => {
    setSelectedFilterSet(null);
  };

  // Parse code sets with numeric values once
  const codeSets = (volumeDataResponse?.code_sets || []).map((cs) => ({
    ...cs,
    claimsNum: parseInt(cs.claims, 10) || 0,
    patientsNum: parseInt(cs.patients, 10) || 0,
    claimsMasked: isMaskedValue(cs.claims),
    patientsMasked: isMaskedValue(cs.patients),
  }));

  // Split code sets into non-zero and zero value groups
  // Masked values (e.g., "<11") should be treated as non-zero
  const nonZeroCodeSets = codeSets.filter(
    (cs) =>
      cs.claimsNum !== 0 ||
      cs.patientsNum !== 0 ||
      cs.claimsMasked ||
      cs.patientsMasked
  );
  const zeroCodeSets = codeSets.filter(
    (cs) =>
      cs.claimsNum === 0 &&
      cs.patientsNum === 0 &&
      !cs.claimsMasked &&
      !cs.patientsMasked
  );

  // Calculate global max for bar scaling (largest single value across all code sets)
  const globalMax = Math.max(
    ...codeSets.flatMap((cs) => [cs.claimsNum, cs.patientsNum]),
    0
  );

  // Determine which code sets to display
  const visibleCodeSets = showZeroCodeSets
    ? [...nonZeroCodeSets, ...zeroCodeSets]
    : nonZeroCodeSets;

  return (
    <div
      className="self-stretch flex flex-col justify-start items-start gap-3 w-full pr-2"
      data-testid="profile-treating-volume-chart"
      role="region"
      aria-label="Code Sets Volume Chart"
    >
      <div
        id="code-sets-list"
        className="self-stretch flex flex-col justify-start items-start gap-3 w-full "
        role="list"
        aria-label="Code sets volume data"
      >
        {visibleCodeSets.map((codeSet, index) => {
          const isValueZero =
            codeSet.claimsNum === 0 || codeSet.patientsNum === 0;

          const bgColor = isValueZero
            ? 'bg-neutral-300'
            : colors[index % colors.length].bgColor;
          const fillColor = isValueZero
            ? 'bg-neutral-400'
            : colors[index % colors.length].fillColor;

          return (
            <TreatingVolumeBar
              key={codeSet.code_set.id}
              codeSet={codeSet}
              bgColor={bgColor}
              fillColor={fillColor}
              globalMax={globalMax}
            />
          );
        })}
      </div>
      {/* Toggle to show/hide zero code sets */}
      {zeroCodeSets.length > 0 && (
        <button
          type="button"
          className="text-primary hover:text-link cursor-pointer text-sm font-normal font-['Inter'] underline leading-5 bg-transparent border-none p-0"
          onClick={() => setShowZeroCodeSets(!showZeroCodeSets)}
          aria-expanded={showZeroCodeSets}
          aria-controls="code-sets-list"
          data-testid="toggle-zero-code-sets"
        >
          {showZeroCodeSets
            ? `Hide ${zeroCodeSets.length}`
            : `Show ${zeroCodeSets.length} hidden`}
        </button>
      )}
      {/* reset if code_sets count is only 1 */}
      {codeSets.length === 1 && (
        <button
          type="button"
          className="text-primary hover:text-link cursor-pointer text-sm font-normal font-['Inter'] underline leading-5 bg-transparent border-none p-0"
          onClick={handleResetFilters}
          data-testid="reset-filter-button"
        >
          Show hidden
        </button>
      )}
    </div>
  );
};

export default ProfileTreatingVolumeChart;
